import { useState, useEffect } from 'react';
import { ShippingLineItem } from '../DeShippingTableWrapper';
import { useOrderData } from './useOrderData';
import { useLineItems } from './useLineItems';
import { bulkAssign } from '../services/assignmentService';
import { getBulkAssignmentOptions } from '../services/inventoryService';
import { useOrderSelection } from './useOrderSelection';

interface StoreOption {
  storeCode: string;
  storeName: string;
  totalAvailable: number;
  totalNeeded: number;
  itemInventory: Array<{
    lineItemId: string;
    productSku: string;
    productName: string;
    quantityNeeded: number;
    quantityAvailable: number;
  }>;
}

interface PartialFulfillmentOption {
  storesInvolved: number;
  totalAvailableAcrossStores: number;
  storeAssignments: Array<{
    storeCode: string;
    storeName: string;
    assignedItems: Array<{
      lineItemId: string;
      productSku: string;
      productName: string;
      quantityAssigned: number;
      quantityAvailable: number;
    }>;
    totalQuantityAssigned: number;
  }>;
}

type AnalysisPhase = 'analyzing' | 'presenting' | 'executing';
type FulfillmentType = 'complete' | 'partial';

interface UseBulkAssignmentParams {
  visible: boolean;
  order: ReturnType<typeof useOrderSelection>['selectedOrder'];
  selectedLineItems: ShippingLineItem[];
  refetch: () => void;
  clearSelection: () => void;
  refreshSingleOrder: (orderId: string) => void;
}

interface UseBulkAssignmentReturn {
  analysisPhase: AnalysisPhase;
  selectedStoreCode: string | null;
  selectedPartialOption: number | null;
  selectedFulfillmentType: FulfillmentType;
  completeOptions: StoreOption[];
  partialOptions: PartialFulfillmentOption[];
  handleStoreSelect: (storeCode: string) => void;
  handlePartialSelect: (optionIndex: number) => void;
  handleFulfillmentTypeChange: (type: FulfillmentType) => void;
  handleExecuteAssignment: () => Promise<{ success: boolean; message?: string; error?: string }>;
}

const analyzeInventoryAvailability = async (orderId: string, lineItemIds: string[]): Promise<{
  completeOptions: StoreOption[];
  partialOptions: PartialFulfillmentOption[];
}> => {
  const data = await getBulkAssignmentOptions(orderId, lineItemIds);
  
  const completeOptions: StoreOption[] = data.complete_fulfillment_options.map((store: any) => ({
    storeCode: store.store_code,
    storeName: store.store_name,
    totalAvailable: store.total_available,
    totalNeeded: store.total_needed,
    itemInventory: store.item_inventory.map((item: any) => ({
      lineItemId: item.line_item_id,
      productSku: item.product_sku,
      productName: item.product_name,
      quantityNeeded: item.quantity_needed,
      quantityAvailable: item.quantity_available
    }))
  }));

  const partialOptions: PartialFulfillmentOption[] = data.partial_fulfillment_options.map((option: any) => ({
    storesInvolved: option.stores_involved,
    totalAvailableAcrossStores: option.total_available_across_stores,
    storeAssignments: option.store_assignments.map((assignment: any) => ({
      storeCode: assignment.store_code,
      storeName: assignment.store_name,
      assignedItems: assignment.assigned_items.map((item: any) => ({
        lineItemId: item.line_item_id,
        productSku: item.product_sku,
        productName: item.product_name,
        quantityAssigned: item.quantity_needed,
        quantityAvailable: item.quantity_available
      })),
      totalQuantityAssigned: assignment.total_quantity_assigned
    }))
  }));
  
  return { completeOptions, partialOptions };
};

export const useBulkAssignment = ({
  visible,
  order,
  selectedLineItems,
  refetch,
  clearSelection,
  refreshSingleOrder
}: UseBulkAssignmentParams): UseBulkAssignmentReturn => {
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>('analyzing');
  const [selectedStoreCode, setSelectedStoreCode] = useState<string | null>(null);
  const [selectedPartialOption, setSelectedPartialOption] = useState<number | null>(null);
  const [selectedFulfillmentType, setSelectedFulfillmentType] = useState<FulfillmentType>('complete');
  const [completeOptions, setCompleteOptions] = useState<StoreOption[]>([]);
  const [partialOptions, setPartialOptions] = useState<PartialFulfillmentOption[]>([]);

  // Create a stable dependency for selected line item IDs
  const selectedLineItemIds = selectedLineItems.map(item => item.line_item_id?.toString() || '').sort().join(',');
  

  
  useEffect(() => {
    if (visible && order && selectedLineItems.length > 0) {
      setAnalysisPhase('analyzing');
      setSelectedStoreCode(null);
      setSelectedPartialOption(null);
      setSelectedFulfillmentType('complete');
      setCompleteOptions([]);
      setPartialOptions([]);
      
      const lineItemIds = selectedLineItems.map(item => item.line_item_id?.toString() || '');
      
      analyzeInventoryAvailability(order.order_id.toString(), lineItemIds)
        .then(({ completeOptions, partialOptions }) => {
          setCompleteOptions(completeOptions);
          setPartialOptions(partialOptions);
          // Auto-select complete fulfillment if available, otherwise partial
          if (completeOptions.length > 0) {
            setSelectedFulfillmentType('complete');
          } else if (partialOptions.length > 0) {
            setSelectedFulfillmentType('partial');
          }
          setAnalysisPhase('presenting');
        })
        .catch((error) => {
          console.error('Failed to analyze inventory:', error);
          setAnalysisPhase('presenting');
        });
    }
  }, [visible, order, selectedLineItemIds]);

  const handleStoreSelect = (storeCode: string) => {
    setSelectedStoreCode(storeCode);
    setSelectedPartialOption(null);
  };

  const handlePartialSelect = (optionIndex: number) => {
    setSelectedPartialOption(optionIndex);
    setSelectedStoreCode(null);
  };

  const handleFulfillmentTypeChange = (type: FulfillmentType) => {
    setSelectedFulfillmentType(type);
    setSelectedStoreCode(null);
    setSelectedPartialOption(null);
  };

  const handleExecuteAssignment = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
    if (!order) {
      return { success: false, error: 'Order not found' };
    }

    if (selectedFulfillmentType === 'complete' && !selectedStoreCode) {
      return { success: false, error: 'No store selected for complete fulfillment' };
    }

    if (selectedFulfillmentType === 'partial' && selectedPartialOption === null) {
      return { success: false, error: 'No partial fulfillment option selected' };
    }
    
    setAnalysisPhase('executing');
    
    try {
      let assignments: Array<{ line_item_id: string; store_name: string }> = [];

      if (selectedFulfillmentType === 'complete') {
        const selectedStore = completeOptions.find(store => store.storeCode === selectedStoreCode);
        if (!selectedStore) {
          throw new Error('Selected store not found');
        }
        
        // Create assignments for all line items to the same store
        assignments = selectedLineItems.map(item => ({
          line_item_id: item.line_item_id?.toString() || '',
          store_name: selectedStore.storeName
        }));
      } else {
        // Handle partial fulfillment - distribute items across multiple stores
        const selectedOption = partialOptions[selectedPartialOption!];
        if (!selectedOption) {
          throw new Error('Selected partial option not found');
        }
        
        // Build assignments based on the partial option's store assignments
        selectedOption.storeAssignments.forEach(storeAssignment => {
          storeAssignment.assignedItems.forEach(item => {
            assignments.push({
              line_item_id: item.lineItemId,
              store_name: storeAssignment.storeName
            });
          });
        });
      }
      
      const result = await bulkAssign(order.order_id.toString(), assignments);
      
      // Reset state
      setAnalysisPhase('analyzing');
      setSelectedStoreCode(null);
      setSelectedPartialOption(null);
      setCompleteOptions([]);
      setPartialOptions([]);
      
      // Refresh data
      clearSelection();
      refetch();
      refreshSingleOrder(order.order_id.toString());
      
      return { 
        success: true, 
        message: result.message || `Items assigned successfully` 
      };
      
    } catch (error) {
      console.error('Failed to execute assignment:', error);
      setAnalysisPhase('presenting');
      
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unable to assign items. Please try again.' 
      };
    }
  };


  return {
    analysisPhase,
    selectedStoreCode,
    selectedPartialOption,
    selectedFulfillmentType,
    completeOptions,
    partialOptions,
    handleStoreSelect,
    handlePartialSelect,
    handleFulfillmentTypeChange,
    handleExecuteAssignment,
  };
};